import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { PermissionGroup } from "../../../../shared/api/permissions";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { formatPermissionLabel } from "../../../../shared/lib";

type PermissionsPickerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  groups: PermissionGroup[];
  isLoading: boolean;
  error: string | null;
  expandedGroupId: string;
  onExpandedGroupIdChange: (value: string) => void;
  isPermissionSelected: (permission: string) => boolean;
  onTogglePermission: (permission: string) => void;
  onToggleGroupPermissions: (permissions: string[]) => void;
  selectedCount: number;
  helperText?: string;
  permissionSuffix?: (permission: string) => string;
  secondaryLabel?: string;
  primaryLabel?: string;
  onSecondaryAction?: () => void;
  onPrimaryAction?: () => void;
};

const PermissionsPickerModal = ({
  open,
  onOpenChange,
  title = "Add permissions",
  searchQuery,
  onSearchQueryChange,
  groups,
  isLoading,
  error,
  expandedGroupId,
  onExpandedGroupIdChange,
  isPermissionSelected,
  onTogglePermission,
  onToggleGroupPermissions,
  selectedCount,
  helperText,
  permissionSuffix,
  secondaryLabel = "Back",
  primaryLabel = "Save permissions",
  onSecondaryAction,
  onPrimaryAction,
}: PermissionsPickerModalProps) => {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={() => onOpenChange(false)}
        >
          <X size={20} />
        </button>
      </ModalHeader>
      <ModalBody className="pt-4">
        <Input
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder="Search permissions"
          startIcon={<Search size={18} className="text-[#7A7FA3]" />}
          className="h-12 rounded-[14px] bg-[#EEF1FB]"
        />

        {helperText ? (
          <div className="mt-3 text-[13px] text-[#6B6F8A]">{helperText}</div>
        ) : null}

        <div className="mt-6 space-y-4">
          {isLoading ? (
            <div className="rounded-[12px] border border-dashed border-[#D7DCF0] p-4 text-center text-[14px] text-[#6B6F8A]">
              Loading permissions...
            </div>
          ) : error ? (
            <div className="rounded-[12px] border border-dashed border-[#F1D6D6] p-4 text-center text-[14px] text-[#C24E4E]">
              {error}
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-[12px] border border-dashed border-[#D7DCF0] p-4 text-center text-[14px] text-[#6B6F8A]">
              No permissions available.
            </div>
          ) : (
            groups.map((group) => {
              const isExpanded = expandedGroupId === group.id;
              const groupSelectedCount = group.permissions.filter(
                (permission) => isPermissionSelected(permission),
              ).length;
              const isAllSelected =
                groupSelectedCount === group.permissions.length &&
                group.permissions.length > 0;

              return (
                <div key={group.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-1 text-left"
                    onClick={() =>
                      onExpandedGroupIdChange(
                        expandedGroupId === group.id ? "" : group.id,
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isAllSelected}
                        onChange={() =>
                          onToggleGroupPermissions(group.permissions)
                        }
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="text-[16px] font-[450] text-[#2C2F44]">
                        {group.label}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={18} className="text-[#6E7397]" />
                    ) : (
                      <ChevronDown size={18} className="text-[#6E7397]" />
                    )}
                  </button>

                  {isExpanded ? (
                    <div className="mx-4 mt-2 rounded-[16px] bg-white pb-4">
                      {group.permissions.map((permission) => (
                        <button
                          key={permission}
                          type="button"
                          className="flex w-full items-center gap-3 px-4 py-3 text-left"
                          onClick={() => onTogglePermission(permission)}
                        >
                          <Checkbox
                            checked={isPermissionSelected(permission)}
                            onChange={() => onTogglePermission(permission)}
                            className="shrink-0"
                            size="sm"
                          />
                          <span className="text-[15px] font-[400] text-[#2C2F44]">
                            {formatPermissionLabel(permission)}
                            {permissionSuffix
                              ? permissionSuffix(permission)
                              : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </ModalBody>
      <ModalFooter className="justify-between">
        <div className="text-[14px] font-[350] text-[#5C638E]">
          {selectedCount} selected
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
            onClick={() =>
              onSecondaryAction ? onSecondaryAction() : onOpenChange(false)
            }
          >
            {secondaryLabel}
          </Button>
          <Button
            className="h-11 rounded-[14px] px-6"
            onClick={() =>
              onPrimaryAction ? onPrimaryAction() : onOpenChange(false)
            }
          >
            {primaryLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default PermissionsPickerModal;
